'use client'

import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../../_components/_ui/background-beams'
import Link from 'next/link'

export default function AuthPage() {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10 container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="mb-8">
                        <Link href="/support/documentation" className="text-indigo-400 hover:text-indigo-300 text-sm">
                            ← Back to Documentation
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-white mb-6">
                        Authentication & Role System
                    </h1>
                    
                    <div className="prose prose-lg prose-invert max-w-none">
                        <p className="text-gray-300 text-lg mb-8">
                            Autofocus implements a sophisticated authentication and authorization system with 
                            role-based permissions, subscription tiers, and granular access controls powered 
                            by Supabase Auth and PostgreSQL Row Level Security.
                        </p>

                        {/* Authentication Overview */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🔐 Authentication System
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Supabase Auth Integration</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Built on Supabase's battle-tested authentication with JWT tokens, OAuth providers, 
                                        and automatic session management.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Supported Methods</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Email/Password authentication</li>
                                                <li>• Google OAuth integration</li>
                                                <li>• GitHub OAuth integration</li>
                                                <li>• Email verification required</li>
                                                <li>• Automatic session refresh</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">Security Features</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• JWT token-based sessions</li>
                                                <li>• Secure HTTP-only cookies</li>
                                                <li>• Automatic token refresh</li>
                                                <li>• Session timeout handling</li>
                                                <li>• Rate limiting on auth endpoints</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Authentication Flow</h3>
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <div className="font-mono text-sm text-gray-300 space-y-2">
                                            <div><span className="text-blue-400">1.</span> User signs up/logs in → Supabase Auth</div>
                                            <div><span className="text-green-400">2.</span> JWT token issued → Stored in secure cookie</div>
                                            <div><span className="text-purple-400">3.</span> Profile created → Username setup required</div>
                                            <div><span className="text-orange-400">4.</span> Role assignment → Default 'basic' subscription</div>
                                            <div><span className="text-cyan-400">5.</span> Dashboard access → Full platform functionality</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Role System */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                👥 Role-Based Permission System
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-3">Role Types & Hierarchy</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Three distinct role categories: permissions (admin access), subscriptions (feature access), 
                                        and special roles (program participation).
                                    </p>
                                    
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-red-400 mb-3">Permission Roles</h4>
                                            <div className="space-y-2 text-sm">
                                                <div className="bg-red-500/10 p-2 rounded">
                                                    <div className="font-semibold text-red-300">admin</div>
                                                    <div className="text-gray-400">Complete platform control</div>
                                                </div>
                                                <div className="bg-orange-500/10 p-2 rounded">
                                                    <div className="font-semibold text-orange-300">moderator</div>
                                                    <div className="text-gray-400">Content moderation access</div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-green-400 mb-3">Subscription Roles</h4>
                                            <div className="space-y-2 text-sm">
                                                <div className="bg-gray-500/10 p-2 rounded">
                                                    <div className="font-semibold text-gray-300">basic</div>
                                                    <div className="text-gray-400">3 pages, 10 blocks</div>
                                                </div>
                                                <div className="bg-blue-500/10 p-2 rounded">
                                                    <div className="font-semibold text-blue-300">turbo</div>
                                                    <div className="text-gray-400">10 pages, 50 blocks</div>
                                                </div>
                                                <div className="bg-purple-500/10 p-2 rounded">
                                                    <div className="font-semibold text-purple-300">summit</div>
                                                    <div className="text-gray-400">Unlimited everything</div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-yellow-400 mb-3">Special Roles</h4>
                                            <div className="space-y-2 text-sm">
                                                <div className="bg-yellow-500/10 p-2 rounded">
                                                    <div className="font-semibold text-yellow-300">alpha</div>
                                                    <div className="text-gray-400">Alpha program access</div>
                                                </div>
                                                <div className="bg-indigo-500/10 p-2 rounded">
                                                    <div className="font-semibold text-indigo-300">beta</div>
                                                    <div className="text-gray-400">Beta testing features</div>
                                                </div>
                                                <div className="bg-pink-500/10 p-2 rounded">
                                                    <div className="font-semibold text-pink-300">beta_plus</div>
                                                    <div className="text-gray-400">Enhanced beta access</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">Permission Matrix</h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-neutral-700">
                                                    <th className="text-left p-2 text-gray-300">Permission</th>
                                                    <th className="text-center p-2 text-red-400">Admin</th>
                                                    <th className="text-center p-2 text-orange-400">Moderator</th>
                                                    <th className="text-center p-2 text-purple-400">Summit</th>
                                                    <th className="text-center p-2 text-blue-400">Turbo</th>
                                                    <th className="text-center p-2 text-gray-400">Basic</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-300">
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-2">Admin Panel Access</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-2">User Management</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-2">Unlimited Pages</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-2">Custom Domains</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-2">Advanced Analytics</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-green-400">✓</td>
                                                    <td className="text-center p-2 text-red-400">✗</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Resource Limits */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                📊 Resource Limits & Subscription Tiers
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-3">Tier Comparison</h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-neutral-700">
                                                    <th className="text-left p-3 text-gray-300">Feature</th>
                                                    <th className="text-center p-3 text-gray-400">Basic (Free)</th>
                                                    <th className="text-center p-3 text-blue-400">Turbo ($6.99/mo)</th>
                                                    <th className="text-center p-3 text-purple-400">Summit ($16.99/mo)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-300">
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-3 font-medium">Pages</td>
                                                    <td className="text-center p-3">3</td>
                                                    <td className="text-center p-3">10</td>
                                                    <td className="text-center p-3 text-green-400">Unlimited</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-3 font-medium">Blocks per Page</td>
                                                    <td className="text-center p-3">10</td>
                                                    <td className="text-center p-3">50</td>
                                                    <td className="text-center p-3 text-green-400">Unlimited</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-3 font-medium">Image Upload Size</td>
                                                    <td className="text-center p-3">1MB</td>
                                                    <td className="text-center p-3">5MB</td>
                                                    <td className="text-center p-3">20MB</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-3 font-medium">Custom Domain</td>
                                                    <td className="text-center p-3 text-red-400">✗</td>
                                                    <td className="text-center p-3 text-red-400">✗</td>
                                                    <td className="text-center p-3 text-green-400">✓</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-3 font-medium">Analytics</td>
                                                    <td className="text-center p-3">Basic</td>
                                                    <td className="text-center p-3">Advanced</td>
                                                    <td className="text-center p-3">Premium</td>
                                                </tr>
                                                <tr className="border-b border-neutral-800">
                                                    <td className="p-3 font-medium">Ad Revenue Share</td>
                                                    <td className="text-center p-3">0%</td>
                                                    <td className="text-center p-3">50%</td>
                                                    <td className="text-center p-3">100%</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Resource Limit Implementation</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-yellow-400 mb-2">Server-Side Validation</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Check if user has reached limit
async function hasReachedLimit(userId, resourceType) {
  const { data: userSub } = await supabase
    .rpc('get_subscription_level', { user_id: userId });
  
  const { data: limits } = await supabase
    .from('resource_limits')
    .select('max_count')
    .eq('role_id', userSub.role_id)
    .eq('resource_type', resourceType)
    .single();
  
  if (limits.max_count === -1) return false; // Unlimited
  
  const { count } = await supabase
    .from(resourceType === 'page' ? 'pages' : 'content_blocks')
    .select('*', { count: 'exact' })
    .eq('profile_id', userId);
  
  return count >= limits.max_count;
}`}
                                            </pre>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-cyan-400 mb-2">Client-Side Enforcement</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// React hook for subscription limits
const { subscriptionLevel, hasMinSubscription } = useAuthRoles();

const canCreatePage = useMemo(() => {
  if (hasMinSubscription('summit')) return true;
  
  return pageCount < getPageLimit(subscriptionLevel);
}, [subscriptionLevel, pageCount]);

// UI state management
if (!canCreatePage) {
  return <UpgradePrompt feature="additional pages" />;
}`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Row Level Security */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🛡️ Row Level Security Implementation
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-3">Database-Level Security</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        PostgreSQL RLS policies ensure data security at the database level, 
                                        preventing unauthorized access even if application logic fails.
                                    </p>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-orange-400 mb-2">User Data Isolation</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`-- Users can only access their own profile data
CREATE POLICY "Users can access own profile" ON profiles
  FOR ALL USING (id = auth.uid());

-- Users can only modify their own pages
CREATE POLICY "Users can manage own pages" ON pages
  FOR ALL USING (profile_id = auth.uid());

-- Content blocks follow page ownership
CREATE POLICY "Users can manage own blocks" ON content_blocks
  FOR ALL USING (
    page_id IN (
      SELECT id FROM pages WHERE profile_id = auth.uid()
    )
  );`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-blue-400 mb-2">Public Content Access</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`-- Public profiles viewable by everyone
CREATE POLICY "Public profiles viewable" ON profiles
  FOR SELECT USING (true);

-- Public pages accessible to all
CREATE POLICY "Public pages viewable" ON pages
  FOR SELECT USING (is_public = true);

-- Public content blocks visible
CREATE POLICY "Public blocks viewable" ON content_blocks
  FOR SELECT USING (
    page_id IN (SELECT id FROM pages WHERE is_public = true)
  );`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">Admin Override Policies</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`-- Admins can access all data for moderation
CREATE POLICY "Admins can access all profiles" ON profiles
  FOR ALL USING (
    EXISTS (
      SELECT 1 FROM user_roles ur
      JOIN roles r ON ur.role_id = r.id
      WHERE ur.user_id = auth.uid() 
      AND r.name = 'admin'
    )
  );`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Authentication Context */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                ⚛️ React Authentication Context
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-3">AuthRolesContext Implementation</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        React context providing authentication state, role information, and 
                                        permission checking throughout the application.
                                    </p>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Context Provider</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`export function AuthRolesProvider({ children }) {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [subscriptionLevel, setSubscriptionLevel] = useState('basic');
  const [loading, setLoading] = useState(true);

  // Check if user has specific role
  const hasRole = (roleName) => {
    if (roleName === 'admin' && user?.user_metadata?.is_admin) {
      return true;
    }
    return roles.some(role => role.role_name === roleName);
  };

  // Check subscription level
  const hasMinSubscription = (level) => {
    const levels = { basic: 0, turbo: 1, summit: 2 };
    return levels[subscriptionLevel] >= levels[level];
  };

  return (
    <AuthRolesContext.Provider value={{
      user, roles, subscriptionLevel, loading,
      hasRole, hasMinSubscription
    }}>
      {children}
    </AuthRolesContext.Provider>
  );
}`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-yellow-400 mb-2">Role-Based Components</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Conditional rendering based on roles
function AdminPanel() {
  const { hasRole } = useAuthRoles();
  
  if (!hasRole('admin')) {
    return <NotAuthorized />;
  }
  
  return <AdminDashboard />;
}

// Subscription-gated features
function PremiumFeature() {
  const { hasMinSubscription } = useAuthRoles();
  
  if (!hasMinSubscription('turbo')) {
    return <UpgradePrompt />;
  }
  
  return <AdvancedAnalytics />;
}`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">
                                🚀 Explore More
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Link href="/support/documentation/database" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-blue-400 mb-1">Database Schema →</div>
                                    <div className="text-gray-300 text-sm">Deep dive into the underlying data structure</div>
                                </Link>
                                <Link href="/support/documentation/infrastructure" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-orange-400 mb-1">Infrastructure →</div>
                                    <div className="text-gray-300 text-sm">Learn about deployment and hosting setup</div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}